import { Box, Display, FlexDirection } from '@gousto-internal/citrus-react'
import type {
  DashboardExperienceActionResponse,
  DashboardExperienceDownloadAppResponse,
  DashboardExperienceRafCampaignResponse,
  DashboardExperienceRafStatusResponse,
} from '@library/api-dashboard-experience'
import React from 'react'

import { DashboardActionButton } from './DashboardActionButton'
import { DownloadAppAction } from './DownloadAppAction'
import { RafCampaignAction, RafStatusAction } from './RafCampaignAction'

type DashboardActionsProps = {
  actions: DashboardExperienceActionResponse[]
  downloadApp?: DashboardExperienceDownloadAppResponse | null
  rafCampaign?: DashboardExperienceRafCampaignResponse | null
  rafStatus?: DashboardExperienceRafStatusResponse | null
  onActionClick?: (actionType: DashboardExperienceActionResponse['type']) => void
}

const canRenderAction = (
  action: DashboardExperienceActionResponse,
  {
    downloadApp,
    rafCampaign,
    rafStatus,
  }: {
    downloadApp?: DashboardExperienceDownloadAppResponse | null
    rafCampaign?: DashboardExperienceRafCampaignResponse | null
    rafStatus?: DashboardExperienceRafStatusResponse | null
  },
) => {
  if (action.type === 'downloadApp') {
    return Boolean(downloadApp)
  }

  if (action.type === 'raf') {
    return Boolean(rafStatus || rafCampaign)
  }

  return true
}

export function DashboardActions({
  actions,
  downloadApp,
  rafCampaign,
  rafStatus,
  onActionClick,
}: DashboardActionsProps) {
  const renderableActions = actions.filter((action) =>
    canRenderAction(action, { downloadApp, rafCampaign, rafStatus }),
  )

  if (!renderableActions.length) {
    return null
  }

  return (
    <Box
      data-testid="dashboard-actions"
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      gap={3}
      width="100%"
    >
      {renderableActions.map((action) => {
        if (action.type === 'downloadApp') {
          if (!downloadApp) {
            return null
          }

          return <DownloadAppAction downloadApp={downloadApp} key={`${action.type}-card`} />
        }

        if (action.type === 'raf') {
          if (rafStatus) {
            return (
              <RafStatusAction
                key={`${action.type}-status`}
                onActionClick={onActionClick}
                status={rafStatus}
              />
            )
          }

          const campaign = rafCampaign

          if (!campaign) {
            return null
          }

          return (
            <RafCampaignAction
              campaign={campaign}
              key={`${action.type}-${campaign.code}`}
              onActionClick={onActionClick}
            />
          )
        }

        return (
          <DashboardActionButton
            actionType={action.type}
            key={`${action.type}-${action.title}`}
            onClick={onActionClick}
          >
            {action.title}
          </DashboardActionButton>
        )
      })}
    </Box>
  )
}
